import React, { useState, useContext, useRef, useMemo, useCallback } from 'react';

import { ALERT_TYPE, CUENTA_STATE, MASK_FORMAT, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE, useForm } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useLista } from '../../components/hooks/useLista';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch,
    Select,
    FormLabel
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import CrearCuentaModal from '../../components/controls/CrearCuentaModal';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import { ManagedContext } from '../../context/custom';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { usePermissions } from '../../components/hooks/usePermissions';
import { PERMISSIONS } from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { unirEnumeracionEs } from '../../utils/formatListaPersonas';
import { buildCementerioEntityInit, FetchUbicacionDefinicion } from '../CementerioView/utils';

function CementeriosView() {

    //#region Hooks
    const { showMessageModal } = useContext(ManagedContext);

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const [tableVisible, setTableVisible] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);
    const [createModal, setCreateModal] = useState({ show: false, idTipoPrestador: 0 });

    const searchRef = useRef();
    const tableRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.CEMENTERIO_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    const [filterUbicacionDef, setFilterUbicacionDef] = useState(null);

    const handleTipoFilterChange = useCallback(({target}) => {
        const id = target.value;
        if (id > 0) {
            FetchUbicacionDefinicion(id)
                .then(def => setFilterUbicacionDef(def))
                .catch(() => setFilterUbicacionDef(null));
        } else {
            setFilterUbicacionDef(null);
        }
    }, []);

    const visibleUbicacionFields = useMemo(() => {
        if (!filterUbicacionDef) return [];
        return Array.from({length: 15}, (_, i) => i + 1)
            .filter(n => filterUbicacionDef[`ubicacionDescripcion${n}`])
            .map(n => `ubicacionValor${n}`);
    }, [filterUbicacionDef]);

    const allUbicacionFilters = useMemo(() =>
        Array.from({length: 15}, (_, i) => ({
            title: filterUbicacionDef?.[`ubicacionDescripcion${i + 1}`] || `Campo ${i + 1}`,
            field: `ubicacionValor${i + 1}`,
            type: 'string',
            size: 2,
            searchOnEnter: true
        })),
    [filterUbicacionDef]);

    const tabsConfig = [
        {
            key: 'cuenta',
            title: 'Cuenta',
            fields: ['numeroCuenta', 'idPersona', 'idEstadoCuenta', 'etiqueta', 'idsTipoCondicionEspecial', 'idsTipoRecargoDescuento'],
            buildUrl: (filters) =>
                `/cuenta/filter?` +
                `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
                `idEstadoCuenta=${filters.idEstadoCuenta}&` +
                `numeroWeb=&numeroDocumento=&` +
                `idPersona=${filters.idPersona}&` +
                `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
                `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
                `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`
        },
        {
            key: 'ubicacion',
            title: 'Ubicación',
            fields: ['idTipoConstruccionFuneraria', ...visibleUbicacionFields],
            buildUrl: (filters) => {
                const parts = Array.from({length: 15}, (_, i) =>
                    filters[`ubicacionValor${i + 1}`] || '%'
                );
                const hasFilter = parts.some(p => p !== '%');
                const catastral = hasFilter ? parts.join('|') : '';
                return `/ubicacion/filter?` +
                    `idTipoConstruccionFuneraria=${filters.idTipoConstruccionFuneraria || 0}&` +
                    `catastral=${encodeURIComponent(catastral)}`;
            }
        },
        {
            key: 'datosInhumado',
            title: 'Datos del inhumado',
            fields: ['numeroDocumentoInhumado', 'nombreApellidoInhumado'],
            buildUrl: (filters) =>
                `/datos/filter?` +
                `numeroDocumentoInhumado=${encodeURIComponent(filters.numeroDocumentoInhumado)}&` +
                `nombreApellidoInhumado=${encodeURIComponent(filters.nombreApellidoInhumado)}`
        }
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            });
    };
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => ({ ...prevState, loading: false }));
    };

    const SearchCementeriosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.CEMENTERIO,
        tabsConfig,
        callbackNoSuccess,
        callbackError,
        setSearchMessage
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchCementeriosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);
    //#endregion

    //definiciones
    const cellA = (props) => !canEdit ? '' : (
        <div className='action'>
            <div onClick={() => handleClickCementerioAdd()} className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    );

    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={() => handleClickCementerioView(props.value)} className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && (
                <>
                    <div onClick={() => handleClickCementerioModify(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>
                    <div onClick={() => handleClickCementerioRemove(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Eliminar">delete</span>
                    </div>
                </>
            )}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA && (
                <div onClick={() => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta)} className="link">
                    <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            )}
            <div onClick={() => handleClickCementerioDataTagger(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    );

    const [, getRowLista] = useLista({
        listas: ['TipoDocumento', 'EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'TipoDocumento_EstadoCuenta',
            timeout: 0
        }
    });

    const getDescTipoDocumento = (id) => {
        const row = getRowLista('TipoDocumento', id);
        return (row) ? row.nombre : '';
    };

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    };

    const formatTitular = (titular) => {
        const nombre = titular.nombre.trim();
        const doc = [getDescTipoDocumento(titular.idTipoDocumento), titular.numeroDocumento].filter(Boolean).join(' ').trim();
        return doc ? `${nombre} (${doc})` : nombre;
    };

    const formatInhumado = (inhumado) => {
        const nombre = [inhumado.nombre, inhumado.apellido].filter(Boolean).join(' ').trim();
        if (inhumado.nombreDesconocido) {
            return nombre ? `${nombre} (NN)` : 'NN';
        }
        return nombre;
    };

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '20%' },
        {
            Header: 'Titular',
            Cell: (props) => unirEnumeracionEs(props.row.original.titulares.map(formatTitular)),
            id: 'titulares',
            accessor: 'id',
            width: '35%'
        },
        {
            Header: 'Inhumado',
            Cell: (props) => unirEnumeracionEs(props.row.original.inhumados.map(formatInhumado), { max: 3 }),
            id: 'inhumados',
            accessor: 'id',
            width: '20%'
        },
        {
            Header: 'Estado cuenta',
            Cell: (props) => getDescEstadoCuenta(props.value),
            accessor: 'idEstadoCuenta',
            width: '15%'
        },
        {
            Header: cellA,
            Cell: cellVMR,
            id: 'abm',
            accessor: 'id',
            width: '10%',
            disableGlobalFilter: true,
            disableSortBy: true
        }
    ];

    //handles
    const handleClickCementerioAdd = () => {
        setCreateModal({ show: true, idTipoPrestador: 0 });
    };

    function CreateCementerio() {
        setState(prevState => ({ ...prevState, loading: true }));
        setCreateModal(prevState => ({ ...prevState, show: false }));

        const base = buildCementerioEntityInit();
        const dataBody = {
            ...base,
            cementerio: { ...base.cementerio, idTipoPrestador: createModal.idTipoPrestador }
        };

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigateWithFilters('/cementerio/' + OPERATION_MODE.EDIT + '/' + row.cementerio.id);
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.CEMENTERIO,
            null,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }
    const handleClickCementerioView = (id) => {
        navigateWithFilters('/cementerio/' + OPERATION_MODE.VIEW + '/' + id);
    };
    const handleClickCementerioModify = (id) => {
        navigateWithFilters('/cementerio/' + OPERATION_MODE.EDIT + '/' + id);
    };
    const handleClickCementerioRemove = (id) => {
        const cementerio = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${cementerio.numeroCuenta}?`,
            onConfirm: () => RemoveCementerio(id),
        });
    };
    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        });
    };
    const handleClickCementerioDataTagger = (id) => {
        setState(prevState => ({ ...prevState, dataTagger: { showModal: true, idEntidad: id } }));
    };

    //funciones
    function SearchCementerios() {
        const tipoId = searchRef.current?.formValues?.idTipoConstruccionFuneraria?.value;
        if (!tipoId && filterUbicacionDef) {
            setFilterUbicacionDef(null);
            const cleanFormValues = { ...searchRef.current.formValues };
            for (let i = 1; i <= 15; i++) {
                cleanFormValues[`ubicacionValor${i}`] = { value: '', label: '' };
            }
            searchRef.current.restoreFilters({
                formValues: cleanFormValues,
                selectedValues: searchRef.current.selectedValues
                    .filter(sv => !sv.field.startsWith('ubicacionValor'))
            });
        }
        return SearchCementeriosWithSavedFilters(getCurrentFilters());
    }

    function RemoveCementerio(id) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    SearchCementerios();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.CEMENTERIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function ReactivarCuenta(idCuenta) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                    SearchCementerios();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerTitulares = unirEnumeracionEs((dataTaggerRow?.titulares ?? []).map(t => t.nombre.trim()))
    const dataTaggerRegistro = dataTaggerTitulares || (dataTaggerRow ? `la cuenta ${dataTaggerRow.numeroCuenta}` : '')

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de cementerio"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_CEMENTERIO_MODAL}
                    entidad="Cementerio"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={!canEdit}
                    onConfirm={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                    onDismiss={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                />
            )}

            {createModal.show && (
                <CrearCuentaModal
                    canConfirm={createModal.idTipoPrestador > 0}
                    onConfirm={CreateCementerio}
                    onDismiss={() => setCreateModal({ show: false, idTipoPrestador: 0 })}
                >
                    <div className="mb-3">
                        <FormLabel htmlFor="idTipoPrestador" required>Tipo de cuenta</FormLabel>
                        <Select
                            name="idTipoPrestador"
                            className="form-control"
                            lista="TipoPrestadorCementerio"
                            value={createModal.idTipoPrestador}
                            onChange={({ target }) => setCreateModal(prevState => ({ ...prevState, idTipoPrestador: target.value }))}
                        />
                    </div>
                </CrearCuentaModal>
            )}

            <SectionHeading
                titles={[{ title: 'Tributos' }, { title: 'Administración de cementerios' }]}
                onAddClick={canEdit ? handleClickCementerioAdd : null}
                tableVisible={tableVisible}
            />

            <section className='section-accordion'>
                <WrapperAdvancedSearch
                    ref={searchRef}
                    message={searchMessage}
                    onClear={() => {
                        setState(prevState => ({ ...prevState, list: [] }));
                        setTableVisible(false);
                    }}
                    onSearch={SearchCementerios}
                    requireFilter={true}
                    tabs={tabsConfig}
                    filters={[
                        { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', size: 3, searchOnEnter: true, mask: MASK_FORMAT.CUENTA },
                        { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                        { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                        { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.CEMENTERIOS, size: 6 },
                        { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },
                        { title: 'Tipo sepultura', field: 'idTipoConstruccionFuneraria', type: 'entity', entidad: 'TipoConstruccionFuneraria', size: 12, onUpdate: handleTipoFilterChange },
                        ...allUbicacionFilters,
                        { title: 'Número de documento', field: 'numeroDocumentoInhumado', type: 'string', mask: MASK_FORMAT.DOCUMENTO, size: 3, searchOnEnter: true },
                        { title: 'Nombre-Apellido', field: 'nombreApellidoInhumado', type: 'string', size: 9, searchOnEnter: true },
                    ]}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className={'TableCustomBase m-top-20'}
                        columns={tableColumns}
                        data={state.list}
                        restoredPageIndex={restoredPageIndex}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default CementeriosView;

